import React, { createContext, useState, useEffect, useContext } from 'react';
import { UserContext } from './UserContext';

export const MembershipContext = createContext({
  membershipTier: 'free', // 'free', 'premium_basic', 'premium_plus'
  isProMember: false,
  loading: true,
  getTradeCompletionFee: (pedalCount = 1) => 3.00 + Math.max(0, pedalCount - 1),
  getCashFeePercentage: () => 6.0,
  getTradeProtectFee: () => 5.00,
  hasPromotedListings: () => false
});

export const MembershipProvider = ({ children }) => {
  const [membershipTier, setMembershipTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const { firestoreUser } = useContext(UserContext);

  // Update membership from Firebase user data
  useEffect(() => {
    if (firestoreUser) {
      const userMembershipTier = firestoreUser.membershipTier || 'free';
      setMembershipTier(userMembershipTier);
      setLoading(false);
    } else {
      setMembershipTier('free');
      setLoading(false);
    }
  }, [firestoreUser]);

  // Calculate if user is a pro member
  const isProMember = membershipTier !== 'free';

  // Fee calculation functions based on membership tier
  const getTradeCompletionFee = (pedalCount = 1) => {
    switch (membershipTier) {
      case 'premium_basic':
      case 'premium_plus':
        return 0.00;
      case 'free':
      default:
        return 3.00 + Math.max(0, pedalCount - 1);
    }
  };

  const getCashFeePercentage = () => {
    switch (membershipTier) {
      case 'premium_basic':
      case 'premium_plus':
        return 4.0; // 4% for premium members
      case 'free':
      default:
        return 6.0; // 6% for free tier
    }
  };

  const getTradeProtectFee = () => {
    // Repriced 2026-09-18 (was 8.50/6.50) to push TradeProtect adoption,
    // paired with the confirm pages defaulting the toggle ON. Server mirror:
    // functions/payoutCalc.js protectionFeeCents() — keep both in sync.
    switch (membershipTier) {
      case 'premium_plus':
        return 3.00; // Reduced fee for 18V members
      case 'premium_basic':
      case 'free':
      default:
        return 5.00; // Full fee for 9V and free tiers
    }
  };

  const hasPromotedListings = () => {
    return membershipTier === 'premium_plus'; // Only 18V members get promoted listings
  };

  const contextValue = {
    membershipTier,
    isProMember,
    loading,
    getTradeCompletionFee,
    getCashFeePercentage,
    getTradeProtectFee,
    hasPromotedListings
  };

  return (
    <MembershipContext.Provider value={contextValue}>
      {children}
    </MembershipContext.Provider>
  );
}; 